<template>
  <div class="relative pb-8">
    <!-- Loading State -->
    <div v-if="pending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <UCard
        v-for="index in 3"
        :key="index"
        :class="[
          index === 1 ? 'hidden sm:block' : '',
          index === 2 ? 'hidden lg:block' : ''
        ]"
        class="h-full p-6 sm:p-8"
      >
        <div class="flex items-center mb-4">
          <USkeleton class="w-20 h-20 rounded-full" />
          <div class="flex-1 ps-4">
            <USkeleton class="h-4 w-24 mb-2" />
            <USkeleton class="h-3 w-32" />
          </div>
        </div>
        <div class="space-y-2">
          <USkeleton class="h-3 w-full" />
          <USkeleton class="h-3 w-full" />
          <USkeleton class="h-3 w-3/4" />
        </div>
      </UCard>
    </div>

    <!-- Error State -->
    <UAlert
      v-else-if="error"
      icon="i-heroicons-exclamation-triangle"
      color="error"
      variant="soft"
      title="Error loading reviews"
      description="Failed to load reviews"
    />

    <!-- Reviews Carousel -->
    <UCarousel
      v-else-if="reviews && reviews.length > 0"
      v-slot="{ item }"
      :items="reviews"
      :ui="{
        item: 'basis-full sm:basis-1/2 lg:basis-1/3 shrink-0 select-none',
        container: '',
        prev: 'left-0! translate-x-[-50%]',
        next: 'right-0! translate-x-[50%]',
        arrows: '',
        dots: 'bottom-0 translate-y-8',
        dot: 'h-3 w-3 data-[state=active]:bg-gray-900 dark:data-[state=active]:bg-white bg-gray-300 dark:bg-gray-600',
      }"
      class="overflow-visible"
      arrows
      dots
    >
      <UCard
        class="h-full rounded-2xl"
        variant="solid"
        :ui="{
          root: 'bg-white dark:bg-neutral-900',
        }"
      >
        <div class="flex items-center mb-4">
          <div class="shrink-0 w-20 h-20 relative">
            <img
              v-if="item.image"
              :src="item.image"
              :alt="item.name"
              class="w-full h-full rounded-full object-cover"
            />
            <div
              v-else
              class="w-full h-full rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
            >
              <UIcon name="i-heroicons-user" class="text-3xl text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div class="ps-4 flex-1">
            <div class="flex items-center mb-2">
              <StarRating :rating="item.rating" />
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">
              {{ item.name }}
            </h3>
          </div>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {{ item.review }}
        </p>
      </UCard>
    </UCarousel>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <UIcon name="i-heroicons-chat-bubble-left-right" class="text-4xl text-gray-400 dark:text-gray-500 mb-3" />
      <p class="text-gray-500 dark:text-gray-400">No reviews available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import reviewsData from '../../../data/reviews.json'
import StarRating from '~/components/molecules/StarRating.vue'

interface Review {
  image: string
  rating: number
  name: string
  review: string
}

const reviews = reviewsData as Review[]
const pending = false
const error = null
</script>

<style scoped>
/* Custom styles for smooth carousel transitions */
:deep(.carousel-item) {
  transition: transform 0.3s ease-in-out;
}
</style>
